const CALCULATE_ENDPOINT = '/api/entrepreneur-eligibility/calculate';
const SAVE_ENDPOINT = '/api/entrepreneur-eligibility/results';
const PENDING_SAVE_KEY = 'loanFitPendingSaveV1';

const UI_TEXT = {
    loading: 'Obrada podataka...',
    empty: 'Rezultat će biti prikazan ovde',
    summaryCopied: 'Sažetak kopiran.',
    summaryCopyFailed: 'Kopiranje nije uspelo. Pokušaj ponovo.',
    summaryMissing: 'Prvo izračunaj rezultat, pa kopiraj sažetak.',
    serviceUnavailable: 'Servis trenutno nije dostupan. Pokušaj ponovo.',
    calculateFallbackError: 'Nismo uspeli da izračunamo. Proveri unose i pokušaj ponovo.',
    savePending: 'Čuvanje rezultata...',
    saveSuccess: 'Rezultat sačuvan.',
    saveFail: 'Rezultat nije sačuvan. Pokušaj ponovo.',
    shareFallbackSuccess: 'Link kopiran.',
    shareFail: 'Deljenje trenutno nije dostupno.',
    postLoginSaveSuccess: 'Rezultat je sačuvan.',
    postLoginSaveFail: 'Rezultat nije sačuvan. Pokušaj ponovo.',
    savedResultsLoading: 'Učitavam sačuvane rezultate...',
    savedResultsEmpty: 'Nemate sačuvanih rezultata.',
    savedResultsError: 'Nije moguće učitati sačuvane rezultate.',
    savedResultsSectionTitle: 'Sačuvani rezultati',
    savedResultsLoadButton: 'Učitaj',
};

const STEP_FIELD_NAMES = {
    1: ['birthYear', 'activityDurationBucket', 'legalFormType', 'businessSector', 'hasLongTermContract', 'clientMix'],
    2: ['monthlyInflows', 'monthlyDebtObligations', 'paymentStyle'],
    3: ['propertyPrice', 'downPayment'],
};

const STEP_TWO_CONDITIONAL_FIELD_BY_PAYMENT_STYLE = {
    regular_salary: 'formalSalaryMonthly',
    licna_zarada: 'formalSalaryMonthly',
    zlp: 'taxBaseMonthly',
    internal_transfer: 'taxBaseMonthly',
    irregular_transfers: 'businessTurnover12m',
    not_paying_self_regularly: 'businessTurnover12m',
};

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : NaN;
};

const toBoolean = (value) => {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    return null;
};

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const getFormValue = (form, fieldName) => {
    const field = form?.elements?.namedItem(fieldName);
    return field && typeof field.value !== 'undefined' ? field.value : '';
};

const formatEur = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return '-';
    }

    return `${Math.round(parsed).toLocaleString('sr-RS')} EUR`;
};

const withOptionalNumber = (target, key, rawValue) => {
    const parsed = toNumber(rawValue);
    if (Number.isFinite(parsed)) {
        target[key] = parsed;
    }
};

const withOptionalBoolean = (target, key, rawValue) => {
    const parsed = toBoolean(rawValue);
    if (typeof parsed === 'boolean') {
        target[key] = parsed;
    }
};

const getStepTwoConditionalFieldName = (paymentStyle) => STEP_TWO_CONDITIONAL_FIELD_BY_PAYMENT_STYLE[paymentStyle] || '';

const getFieldNode = (form, fieldName) => {
    const node = form?.elements?.namedItem(fieldName);
    return node || null;
};

const buildPayloadFromForm = (form) => {
    const payload = {
        birthYear: toNumber(getFormValue(form, 'birthYear')),
        propertyPrice: toNumber(getFormValue(form, 'propertyPrice')),
        downPayment: toNumber(getFormValue(form, 'downPayment')),
        monthlyInflows: toNumber(getFormValue(form, 'monthlyInflows')),
        monthlyDebtObligations: toNumber(getFormValue(form, 'monthlyDebtObligations')),
        paymentStyle: getFormValue(form, 'paymentStyle').trim(),
        legalFormType: getFormValue(form, 'legalFormType').trim(),
        activityDurationBucket: getFormValue(form, 'activityDurationBucket').trim(),
        businessSector: getFormValue(form, 'businessSector').trim(),
        clientMix: getFormValue(form, 'clientMix').trim(),
        hasLongTermContract: toBoolean(getFormValue(form, 'hasLongTermContract')),
    };

    withOptionalBoolean(payload, 'privateInflowsLookLikeSalary', getFormValue(form, 'privateInflowsLookLikeSalary'));
    withOptionalBoolean(payload, 'profitOrDividendInsteadOfSalary', getFormValue(form, 'profitOrDividendInsteadOfSalary'));

    const conditionalFieldName = getStepTwoConditionalFieldName(payload.paymentStyle);
    if (conditionalFieldName) {
        withOptionalNumber(payload, conditionalFieldName, getFormValue(form, conditionalFieldName));
    }

    withOptionalNumber(payload, 'businessTurnover24m', getFormValue(form, 'businessTurnover24m'));

    return payload;
};

const setInlineMessage = (node, text, tone = 'warn') => {
    if (!node) return;

    if (!text) {
        node.hidden = true;
        node.textContent = '';
        node.classList.remove('loan-fit-status-success', 'loan-fit-status-error');
        return;
    }

    node.hidden = false;
    node.textContent = text;
    node.classList.remove('loan-fit-status-success', 'loan-fit-status-error');
    if (tone === 'success') {
        node.classList.add('loan-fit-status-success');
    } else if (tone === 'error') {
        node.classList.add('loan-fit-status-error');
    }
};

const setResultsVisualState = (refs, state) => {
    const isLoading = state === 'loading';
    const isError = state === 'error';
    const isSuccess = state === 'success';
    const isEmpty = !isLoading && !isError && !isSuccess;

    refs.loadingNode.hidden = !isLoading;
    refs.errorNode.hidden = !isError;
    refs.emptyNode.hidden = !isEmpty;
    refs.successNode.hidden = !isSuccess;
    refs.resultsNode.setAttribute('data-state', state);
};

const setSavedResultsState = (refs, visState) => {
    const isLoading = visState === 'loading';
    const isEmpty = visState === 'empty';
    const isError = visState === 'error';
    const isList = visState === 'list';

    if (refs.savedResultsLoadingNode) refs.savedResultsLoadingNode.hidden = !isLoading;
    if (refs.savedResultsEmptyNode)   refs.savedResultsEmptyNode.hidden  = !isEmpty;
    if (refs.savedResultsErrorNode)   refs.savedResultsErrorNode.hidden  = !isError;
    if (refs.savedResultsList)        refs.savedResultsList.hidden        = !isList;
};

const renderSavedResultsList = (refs, items) => {
    if (!refs.savedResultsList) return;

    refs.savedResultsList.innerHTML = items.map((item, index) => {
        const d = new Date(item.createdAt);
        const dateStr = `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}.`;
        return `<li class="loan-fit-saved-result-row">
            <span class="loan-fit-saved-result-date">${escapeHtml(dateStr)}</span>
            <button
                type="button"
                class="loan-fit-btn loan-fit-saved-result-load"
                data-action="load-saved-result"
                data-index="${index}"
            >${escapeHtml(UI_TEXT.savedResultsLoadButton)}</button>
        </li>`;
    }).join('');

    refs.savedResultsLoadButtons = Array.from(
        refs.savedResultsList.querySelectorAll('[data-action="load-saved-result"]')
    );

    refs.savedResultsLoadButtons.forEach((btn) => {
        btn.disabled = refs.isPending;
    });

    refs.savedResultsLoadButtons.forEach((btn) => {
        const index = Number(btn.getAttribute('data-index'));
        btn.addEventListener('click', () => {
            if (refs.isPending) return;
            const item = items[index];
            if (!item) return;
            renderResult(item.inputJson, item.outputJson, refs);
            refs.resultsNode?.scrollIntoView({ behavior: 'smooth' });
        });
    });
};

const fetchSavedResults = async (refs, state) => {
    if (!state.isAuthenticated) return;
    if (!refs.savedResultsSection) return;

    refs.savedResultsSection.hidden = false;
    setSavedResultsState(refs, 'loading');
    if (refs.savedResultsLoadingNode?.querySelector('p')) {
        refs.savedResultsLoadingNode.querySelector('p').textContent = UI_TEXT.savedResultsLoading;
    }

    try {
        const response = await fetch(`${SAVE_ENDPOINT}?limit=20&offset=0`, {
            credentials: 'include',
        });

        if (!response.ok) {
            if (refs.savedResultsErrorNode) {
                refs.savedResultsErrorNode.textContent = UI_TEXT.savedResultsError;
            }
            setSavedResultsState(refs, 'error');
            return;
        }

        const data = await response.json();
        const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);

        if (items.length === 0) {
            if (refs.savedResultsEmptyNode?.querySelector('p')) {
                refs.savedResultsEmptyNode.querySelector('p').textContent = UI_TEXT.savedResultsEmpty;
            }
            setSavedResultsState(refs, 'empty');
            return;
        }

        renderSavedResultsList(refs, items);
        setSavedResultsState(refs, 'list');
    } catch (_error) {
        if (refs.savedResultsErrorNode) {
            refs.savedResultsErrorNode.textContent = UI_TEXT.savedResultsError;
        }
        setSavedResultsState(refs, 'error');
    }
};

const renderBankItems = (listNode, items = []) => {
    if (!listNode) return;

    if (!Array.isArray(items) || items.length === 0) {
        listNode.innerHTML = '<li class="loan-fit-bank-item">Nema izdvojenih banaka.</li>';
        return;
    }

    listNode.innerHTML = items.slice(0, 5).map((item) => `<li class="loan-fit-bank-item">
        <div class="loan-fit-bank-head">
            <span class="loan-fit-bank-name">${escapeHtml(item.bankName || 'Banka')}</span>
            ${item.label ? `<span class="loan-fit-bank-label">${escapeHtml(item.label)}</span>` : ''}
        </div>
        <p class="loan-fit-bank-one-liner">${escapeHtml(item.oneLiner || 'Pripremi kompletnu dokumentaciju i stabilne prilive.')}</p>
    </li>`).join('');
};

const renderDetailList = (listNode, items = [], emptyText) => {
    if (!listNode) return;

    if (!Array.isArray(items) || items.length === 0) {
        listNode.innerHTML = `<li class="loan-fit-detail-item">${escapeHtml(emptyText)}</li>`;
        return;
    }

    listNode.innerHTML = items.map((item) => `<li class="loan-fit-detail-item">${escapeHtml(item)}</li>`).join('');
};

const focusFirstFieldForStep = (step, refs) => {
    const fieldNames = getStepValidationKeys(step, buildPayloadFromForm(refs.form));

    for (let i = 0; i < fieldNames.length; i += 1) {
        const field = getFieldNode(refs.form, fieldNames[i]);
        if (field && !field.hidden && !field.disabled && typeof field.focus === 'function') {
            field.focus();
            return;
        }
    }
};

const syncStepTabDisabledState = (refs) => {
    refs.stepTabs.forEach((tab) => {
        const tabStep = Number(tab.getAttribute('data-step-target'));
        const isLocked = tabStep > refs.currentStep;
        tab.setAttribute('aria-disabled', isLocked ? 'true' : 'false');
    });
};

const setActiveStep = (nextStep, refs) => {
    refs.currentStep = nextStep;
    refs.stepPanels.forEach((panel) => {
        const panelStep = Number(panel.getAttribute('data-step-panel'));
        panel.hidden = panelStep !== nextStep;
    });

    refs.stepTabs.forEach((tab) => {
        const tabStep = Number(tab.getAttribute('data-step-target'));
        const isActive = tabStep === nextStep;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
    });

    syncStepTabDisabledState(refs);

    if (refs.stepKicker) {
        refs.stepKicker.textContent = `Korak ${nextStep} od 3`;
    }

    if (refs.formRoot) {
        refs.formRoot.setAttribute('data-current-step', String(nextStep));
    }

    focusFirstFieldForStep(nextStep, refs);
};

const updateStepTwoConditionalField = (refs) => {
    const paymentStyle = getFormValue(refs.form, 'paymentStyle').trim();
    const activeFieldName = getStepTwoConditionalFieldName(paymentStyle);

    refs.stepTwoConditionalFields.forEach((fieldGroup) => {
        const fieldName = fieldGroup.getAttribute('data-conditional-income-field');
        const input = refs.form.elements.namedItem(fieldName);
        const isActive = fieldName === activeFieldName;

        fieldGroup.hidden = !isActive;

        if (input && typeof input.required !== 'undefined') {
            input.required = isActive;
        }

        if (!isActive && input && typeof input.value !== 'undefined') {
            input.value = '';
        }
    });
};

const getStepValidationKeys = (step, payload) => {
    const baseKeys = STEP_FIELD_NAMES[step] || [];

    if (step !== 2) {
        return baseKeys;
    }

    const conditionalFieldName = getStepTwoConditionalFieldName(payload.paymentStyle);
    return conditionalFieldName ? [...baseKeys, conditionalFieldName] : baseKeys;
};

const getStepValidationMessage = (step, payload) => {
    const requiredKeys = getStepValidationKeys(step, payload);

    for (let i = 0; i < requiredKeys.length; i += 1) {
        const key = requiredKeys[i];
        const value = payload[key];

        if (typeof value === 'number') {
            if (!Number.isFinite(value)) {
                return 'Popuni sva obavezna polja u ovom koraku.';
            }

            if (value < 0) {
                return 'Polja ne mogu imati negativne vrednosti.';
            }

            continue;
        }

        if (typeof value === 'string') {
            if (!value.trim()) {
                return 'Popuni sva obavezna polja u ovom koraku.';
            }
            continue;
        }

        if (typeof value === 'boolean') {
            continue;
        }

        return 'Popuni sva obavezna polja u ovom koraku.';
    }

    if (step === 3 && payload.downPayment >= payload.propertyPrice) {
        return 'Učešće mora biti manje od cene nekretnine.';
    }

    return '';
};

const getFieldConstraintMessage = (fieldName, fieldValue, form) => {
    const field = getFieldNode(form, fieldName);
    if (!field || typeof fieldValue !== 'number' || !Number.isFinite(fieldValue)) {
        return '';
    }

    const minRaw = typeof field.getAttribute === 'function' ? field.getAttribute('min') : null;
    const maxRaw = typeof field.getAttribute === 'function' ? field.getAttribute('max') : null;
    const min = minRaw !== null && minRaw !== '' ? Number(minRaw) : NaN;
    const max = maxRaw !== null && maxRaw !== '' ? Number(maxRaw) : NaN;

    if (Number.isFinite(min) && fieldValue < min) {
        return fieldName === 'birthYear'
            ? `Godina rođenja mora biti između ${min} i ${max}.`
            : 'Uneta vrednost je ispod dozvoljenog minimuma.';
    }

    if (Number.isFinite(max) && fieldValue > max) {
        return fieldName === 'birthYear'
            ? `Godina rođenja mora biti između ${min} i ${max}.`
            : 'Uneta vrednost je iznad dozvoljenog maksimuma.';
    }

    return '';
};

const getStepByFieldName = (fieldName) => {
    if (STEP_FIELD_NAMES[1].includes(fieldName)) {
        return 1;
    }

    if (STEP_FIELD_NAMES[2].includes(fieldName) || ['formalSalaryMonthly', 'taxBaseMonthly', 'businessTurnover12m'].includes(fieldName)) {
        return 2;
    }

    return 3;
};

const getSubmitValidationResult = (payload, refs) => {
    for (let step = 1; step <= 3; step += 1) {
        const stepMessage = getStepValidationMessage(step, payload);
        if (stepMessage) {
            return { message: stepMessage, step };
        }
    }

    const keysToCheck = [
        ...getStepValidationKeys(1, payload),
        ...getStepValidationKeys(2, payload),
        ...getStepValidationKeys(3, payload),
    ];

    for (let i = 0; i < keysToCheck.length; i += 1) {
        const key = keysToCheck[i];
        const constraintMessage = getFieldConstraintMessage(key, payload[key], refs.form);
        if (constraintMessage) {
            return { message: constraintMessage, step: getStepByFieldName(key) };
        }
    }

    return { message: '', step: 3 };
};

const savePendingResult = (payload, result) => {
    try {
        window.sessionStorage.setItem(PENDING_SAVE_KEY, JSON.stringify({ payload, result }));
    } catch (_error) {
        // Non-fatal if storage is unavailable.
    }
};

const readPendingResult = () => {
    try {
        const raw = window.sessionStorage.getItem(PENDING_SAVE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (_error) {
        return null;
    }
};

const clearPendingResult = () => {
    try {
        window.sessionStorage.removeItem(PENDING_SAVE_KEY);
    } catch (_error) {
        // Ignore storage cleanup errors.
    }
};

const setPendingState = (refs, isPending) => {
    refs.isPending = isPending;

    const controls = [
        ...(refs.stepActionButtons || []),
        ...(refs.submitButtons || []),
        refs.saveButton,
        ...(refs.savedResultsLoadButtons || []),
    ].filter(Boolean);

    controls.forEach((control) => {
        control.disabled = isPending;
    });
};

const resolveDisclaimerTextFromDom = (refs) => {
    return document.querySelector('.loan-fit-page')?.getAttribute('data-disclaimer-text')?.trim() || '';
};

async function postJson(url, payload) {
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        const fallbackMessage = UI_TEXT.serviceUnavailable;
        let message = fallbackMessage;

        try {
            const json = await response.json();
            if (json?.message) {
                message = json.message;
            }
        } catch (_error) {
            // Keep fallback text.
        }

        throw new Error(message);
    }

    return response.json();
}

const setupActionButtons = (refs) => {
    refs.copySummaryButton?.addEventListener('click', async () => {
        if (!refs.lastResult) {
            setInlineMessage(refs.saveStatus, UI_TEXT.summaryMissing, 'error');
            return;
        }

        const summaryText = [
            refs.summaryLine?.textContent || '',
            ...Array.from(refs.priorityList?.querySelectorAll('.loan-fit-bank-name') || []).map((node) => node.textContent || ''),
            ...Array.from(refs.weakPointsList?.querySelectorAll('li') || []).map((node) => node.textContent || ''),
            ...Array.from(refs.prepareChecklistList?.querySelectorAll('li') || []).map((node) => node.textContent || '')
        ].map((value) => String(value).trim()).filter(Boolean).join('\n');

        if (!summaryText) {
            setInlineMessage(refs.saveStatus, UI_TEXT.summaryMissing, 'error');
            return;
        }

        try {
            await navigator.clipboard.writeText(summaryText);
            setInlineMessage(refs.saveStatus, UI_TEXT.summaryCopied, 'success');
        } catch (_error) {
            setInlineMessage(refs.saveStatus, UI_TEXT.summaryCopyFailed, 'error');
        }
    });

    refs.shareButton?.addEventListener('click', async () => {
        const shareData = {
            title: 'Rezultat provere',
            text: 'Banke za prvi kontakt.',
            url: window.location.href,
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
                return;
            } catch (_error) {
                // Fallback to clipboard below.
            }
        }

        try {
            await navigator.clipboard.writeText(window.location.href);
            setInlineMessage(refs.saveStatus, UI_TEXT.shareFallbackSuccess, 'success');
        } catch (_error) {
            setInlineMessage(refs.saveStatus, UI_TEXT.shareFail, 'error');
        }
    });
};

const buildLoginRedirectUrl = () => {
    const nextParams = new URLSearchParams(window.location.search);
    nextParams.set('pendingSave', '1');

    const nextPath = `${window.location.pathname}?${nextParams.toString()}`;
    const loginParams = new URLSearchParams();
    loginParams.set('next', nextPath);

    return `/login?${loginParams.toString()}`;
};

const saveResult = async (refs, state) => {
    if (refs.isPending) {
        return;
    }

    if (!refs.lastPayload || !refs.lastResult) {
        setInlineMessage(refs.saveStatus, 'Nema rezultata za čuvanje. Prvo izračunaj rezultat.', 'error');
        return;
    }

    if (!state.isAuthenticated) {
        savePendingResult(refs.lastPayload, refs.lastResult);
        const loginRedirectUrl = buildLoginRedirectUrl();
        window.__loanFitRedirectTarget = loginRedirectUrl;
        window.location.assign(loginRedirectUrl);
        return;
    }

    setPendingState(refs, true);
    setInlineMessage(refs.saveStatus, UI_TEXT.savePending, 'warn');

    try {
        await postJson(SAVE_ENDPOINT, {
            inputJson: refs.lastPayload,
            outputJson: refs.lastResult,
        });
        clearPendingResult();
        setInlineMessage(refs.saveStatus, UI_TEXT.saveSuccess, 'success');
    } catch (_error) {
        setInlineMessage(refs.saveStatus, UI_TEXT.saveFail, 'error');
    } finally {
        setPendingState(refs, false);
    }
};

const renderResult = (payload, result, refs) => {
    setResultsVisualState(refs, 'success');

    if (refs.summaryLine) {
        refs.summaryLine.textContent = 'Popuni sva 3 koraka za rezultat';
    }

    renderBankItems(refs.priorityList, result.gdePrvoDaPokusas);
    renderDetailList(refs.weakPointsList, (result.slabaTacka || []).slice(0, 3), 'Nema izdvojenih prepreka.');
    renderDetailList(refs.prepareChecklistList, result.pripremaZaBanku, 'Nema dodatne dokumentacije za prikaz.');

    if (refs.disclaimerNode && !refs.disclaimerNode.textContent.trim()) {
        refs.disclaimerNode.textContent = refs.defaultDisclaimerText;
    }

    const financingNeed = Math.max(0, (Number(payload.propertyPrice) || 0) - (Number(payload.downPayment) || 0));

    if (refs.financingMetric) {
        refs.financingMetric.textContent = formatEur(financingNeed);
    }

    if (refs.inflowsMetric) {
        refs.inflowsMetric.textContent = formatEur(payload.monthlyInflows);
    }

    if (refs.obligationsMetric) {
        refs.obligationsMetric.textContent = formatEur(payload.monthlyDebtObligations);
    }

    const monthlyHref = financingNeed > 0
        ? `/calculators/fixed?loanAmount=${Math.round(financingNeed)}&termYears=30&annualRate=5`
        : '/calculators/fixed';
    if (refs.monthlyCta) {
        refs.monthlyCta.setAttribute('href', monthlyHref);
    }

    const costHref = Number(payload.propertyPrice) > 0
        ? `/notary-costs?propertyPrice=${encodeURIComponent(String(Math.round(payload.propertyPrice)))}`
        : '/notary-costs';
    if (refs.totalCostCta) {
        refs.totalCostCta.setAttribute('href', costHref);
    }

    refs.lastPayload = payload;
    refs.lastResult = result;
};

const submitForm = async (event, refs) => {
    event.preventDefault();

    if (refs.isPending) {
        return;
    }

    const payload = buildPayloadFromForm(refs.form);
    const { message: submitValidationMessage, step: invalidStep } = getSubmitValidationResult(payload, refs);

    if (submitValidationMessage) {
        setInlineMessage(refs.formMessage, submitValidationMessage, 'error');
        setActiveStep(invalidStep, refs);
        return;
    }

    setInlineMessage(refs.formMessage, '');
    setInlineMessage(refs.saveStatus, '');

    refs.resultsNode.setAttribute('aria-busy', 'true');
    setResultsVisualState(refs, 'loading');
    setPendingState(refs, true);

    try {
        const result = await postJson(CALCULATE_ENDPOINT, payload);
        renderResult(payload, result, refs);
    } catch (error) {
        setResultsVisualState(refs, 'error');
        refs.errorNode.textContent = error.message || UI_TEXT.calculateFallbackError;
    } finally {
        refs.resultsNode.setAttribute('aria-busy', 'false');
        setPendingState(refs, false);
    }
};

const handleStepAction = (action, refs) => {
    const payload = buildPayloadFromForm(refs.form);

    if (action === 'next') {
        const validationMessage = getStepValidationMessage(refs.currentStep, payload);
        if (validationMessage) {
            setInlineMessage(refs.formMessage, validationMessage, 'error');
            return;
        }

        setInlineMessage(refs.formMessage, '');
        setActiveStep(Math.min(3, refs.currentStep + 1), refs);
        return;
    }

    if (action === 'back') {
        setInlineMessage(refs.formMessage, '');
        setActiveStep(Math.max(1, refs.currentStep - 1), refs);
    }
};

const handleStepTabKeydown = (event, refs) => {
    const currentIndex = refs.stepTabs.findIndex((tab) => tab === event.currentTarget);
    if (currentIndex < 0) return;

    let targetIndex = -1;

    if (event.key === 'ArrowRight') {
        targetIndex = Math.min(refs.stepTabs.length - 1, currentIndex + 1);
    } else if (event.key === 'ArrowLeft') {
        targetIndex = Math.max(0, currentIndex - 1);
    } else if (event.key === 'Home') {
        targetIndex = 0;
    } else if (event.key === 'End') {
        targetIndex = refs.stepTabs.length - 1;
    }

    if (targetIndex < 0) {
        return;
    }

    event.preventDefault();
    const targetTab = refs.stepTabs[targetIndex];
    if (!targetTab) return;

    targetTab.focus();

    const targetStep = Number(targetTab.getAttribute('data-step-target'));
    if (targetStep <= refs.currentStep) {
        setInlineMessage(refs.formMessage, '');
        setActiveStep(targetStep, refs);
    }
};

const tryAutoSaveAfterLogin = async (refs, state) => {
    if (!state.isAuthenticated) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get('pendingSave') !== '1') return;

    const pending = readPendingResult();
    if (!pending || !pending.payload || !pending.result) {
        url.searchParams.delete('pendingSave');
        const search = url.searchParams.toString();
        window.history.replaceState({}, '', search ? `${url.pathname}?${search}` : url.pathname);
        return;
    }

    refs.lastPayload = pending.payload;
    refs.lastResult = pending.result;
    setPendingState(refs, true);

    try {
        await postJson(SAVE_ENDPOINT, {
            inputJson: pending.payload,
            outputJson: pending.result,
        });
        clearPendingResult();
        setInlineMessage(refs.saveStatus, UI_TEXT.postLoginSaveSuccess, 'success');
        renderResult(pending.payload, pending.result, refs);
        await fetchSavedResults(refs, state);
        refs.resultsNode?.scrollIntoView({ behavior: 'smooth' });
    } catch (_error) {
        setInlineMessage(refs.saveStatus, UI_TEXT.postLoginSaveFail, 'error');
    }

    url.searchParams.delete('pendingSave');
    const nextSearch = url.searchParams.toString();
    window.history.replaceState({}, '', nextSearch ? `${url.pathname}?${nextSearch}` : url.pathname);
    setPendingState(refs, false);
};

export function initLoanFitUI(state = {}) {
    const form = document.getElementById('loan-fit-form');
    if (!form) return;

    const refs = {
        formRoot: document.querySelector('.loan-fit-shell'),
        form,
        stepKicker: document.getElementById('loan-fit-step-kicker'),
        formMessage: document.getElementById('loan-fit-form-message'),
        resultsNode: document.getElementById('loan-fit-results'),
        summaryLine: document.getElementById('loan-fit-summary-line'),
        financingMetric: document.getElementById('loan-fit-metric-financing'),
        inflowsMetric: document.getElementById('loan-fit-metric-inflows'),
        obligationsMetric: document.getElementById('loan-fit-metric-obligations'),
        loadingNode: document.getElementById('loan-fit-loading'),
        emptyNode: document.getElementById('loan-fit-empty'),
        errorNode: document.getElementById('loan-fit-error'),
        successNode: document.getElementById('loan-fit-success'),
        disclaimerNode: document.getElementById('loan-fit-disclaimer-result'),
        priorityList: document.getElementById('loan-fit-priority-list'),
        weakPointsList: document.getElementById('loan-fit-weak-points'),
        prepareChecklistList: document.getElementById('loan-fit-prepare-checklist'),
        saveButton: document.getElementById('loan-fit-save-result'),
        saveStatus: document.getElementById('loan-fit-save-status'),
        shareButton: document.getElementById('loan-fit-share'),
        copySummaryButton: document.getElementById('loan-fit-copy-summary'),
        monthlyCta: document.getElementById('loan-fit-cta-monthly'),
        totalCostCta: document.getElementById('loan-fit-cta-cost'),
        stepTabs: Array.from(document.querySelectorAll('[data-step-target]')),
        stepPanels: Array.from(document.querySelectorAll('[data-step-panel]')),
        stepActionButtons: Array.from(form.querySelectorAll('[data-step-action]')),
        submitButtons: Array.from(form.querySelectorAll('[type="submit"]')),
        stepTwoConditionalFields: Array.from(form.querySelectorAll('[data-conditional-income-field]')),
        currentStep: 1,
        lastPayload: null,
        lastResult: null,
        isPending: false,
        defaultDisclaimerText: '',
        savedResultsSection: document.getElementById('loan-fit-saved-results'),
        savedResultsList: document.getElementById('loan-fit-saved-results-list'),
        savedResultsLoadingNode: document.getElementById('loan-fit-saved-results-loading'),
        savedResultsEmptyNode: document.getElementById('loan-fit-saved-results-empty'),
        savedResultsErrorNode: document.getElementById('loan-fit-saved-results-error'),
        savedResultsLoadButtons: [],
    };

    refs.defaultDisclaimerText = resolveDisclaimerTextFromDom(refs);

    refs.form.addEventListener('submit', (event) => submitForm(event, refs));

    refs.stepActionButtons.forEach((button) => {
        button.addEventListener('click', () => {
            const action = button.getAttribute('data-step-action');
            handleStepAction(action, refs);
        });
    });

    refs.stepTabs.forEach((tab) => {
        tab.addEventListener('click', () => {
            const targetStep = Number(tab.getAttribute('data-step-target'));
            if (targetStep <= refs.currentStep) {
                setInlineMessage(refs.formMessage, '');
                setActiveStep(targetStep, refs);
            }
        });

        tab.addEventListener('keydown', (event) => {
            handleStepTabKeydown(event, refs);
        });
    });

    refs.form.elements.namedItem('paymentStyle')?.addEventListener('change', () => {
        updateStepTwoConditionalField(refs);
    });

    setResultsVisualState(refs, 'empty');
    if (refs.loadingNode?.querySelector('p')) {
        refs.loadingNode.querySelector('p').textContent = UI_TEXT.loading;
    }
    if (refs.emptyNode?.querySelector('p')) {
        refs.emptyNode.querySelector('p').textContent = UI_TEXT.empty;
    }
    updateStepTwoConditionalField(refs);
    syncStepTabDisabledState(refs);

    refs.saveButton?.addEventListener('click', () => saveResult(refs, state));

    setupActionButtons(refs);
    tryAutoSaveAfterLogin(refs, state);
    if (state.isAuthenticated) {
        fetchSavedResults(refs, state);
    }
}
